import type { WallId } from "./wallDefinitions";

const MINUTE_MS = 60 * 1000;

interface WallBalanceProfileCommon {
  readonly id: WallId;
  readonly displayName: string;
  readonly reward: {
    readonly targetClearMs: number;
    readonly caps: {
      readonly base: number;
      readonly relaxed: number;
    };
    readonly [key: string]: unknown;
  };
  readonly [key: string]: unknown;
}

// Wall balance SSOT. These profiles intentionally keep each Wall's knobs
// independent even when two Walls currently use the same formula shape.
// Formula ownership remains in the Wall-specific logic modules; this file owns
// the numeric inputs that designers tune and compare.
export const STEP_WALL_BALANCE_PROFILE = {
  id: "step-wall",
  displayName: "Step Wall",
  firstClearReward: { kind: "hov-core", amount: 10 },
  challenge: {
    blockedCellIndices: [1, 3, 5, 7] as readonly number[],
  },
  reward: {
    softcapClearMs: 1 * MINUTE_MS,
    targetClearMs: 30 * MINUTE_MS,
    baseMultiplier: 1.5,
    acceleration: 8,
    exponent: 1.2,
    caps: {
      base: 1000,
      relaxed: 10000,
    },
  },
} as const;

export const PRICE_WALL_BALANCE_PROFILE = {
  id: "price-wall",
  displayName: "Price Wall",
  firstClearReward: { kind: "hov-core", amount: 20 },
  reward: {
    targetClearMs: 30 * MINUTE_MS,
    fastClearMs: 1 * MINUTE_MS,
    minimumMultiplier: 2,
    targetMultiplier: 3,
    curveRange: 12,
    curveExponent: 0.8,
    caps: {
      base: 15,
      relaxed: 30,
    },
  },
} as const;

export const FRESHNESS_WALL_BALANCE_PROFILE = {
  id: "freshness-wall",
  displayName: "Freshness Wall",
  firstClearReward: { kind: "hov-core", amount: 40 },
  challenge: {
    mHovStaleDurationMs: 10_000,
    mHovStaleFloorExponent: 0.001,
    nHovStaleDurationMs: 30_000,
    nHovStaleFloorExponent: 0.01,
  },
  reward: {
    softcapClearMs: 0.25 * MINUTE_MS,
    targetClearMs: 30 * MINUTE_MS,
    baseExponentBonus: 0.01,
    minimumExponentBonus: 0.01,
    falloff: 0.677,
    caps: {
      base: 0.3,
      relaxed: 1.0,
    },
  },
} as const;

export const MP_WALL_BALANCE_PROFILE = {
  id: "mp-wall",
  displayName: "MP Wall",
  firstClearReward: { kind: "hov-core", amount: 100 },
  reward: {
    softcapClearMs: 5 * MINUTE_MS,
    targetClearMs: 15 * MINUTE_MS,
    minimumMpBonus: 1,
    caps: {
      base: 10,
      relaxed: 20,
    },
  },
} as const;

export const OUTER_WALL_BALANCE_PROFILE = {
  id: "outer-wall",
  displayName: "Outer Wall",
  firstClearReward: { kind: "ohov-mp-control-unlock" },
  challenge: {
    clearTargetHov: "4.2e720",
  },
  reward: {
    targetClearMs: 20 * MINUTE_MS,
    minimumTier: 2,
    softcapSpeedRatio: 4,
    targetBaseMantissa: 1.8,
    targetExponentStep: 308,
    caps: {
      base: 8,
      relaxed: 11,
    },
  },
} as const;

export const SPLIT_WALL_BALANCE_PROFILE = {
  id: "split-wall",
  displayName: "Split Wall",
  firstClearReward: { kind: "hov-core", amount: 200 },
  challenge: { clearTargetHov: "3.9e660" },
  reward: {
    softcapClearMs: 1 * MINUTE_MS,
    targetClearMs: 20 * MINUTE_MS,
    baseMultiplier: 1.5,
    acceleration: 8,
    exponent: 1.2,
    caps: {
      base: 500,
      relaxed: 5000,
    },
  },
} as const;

export const HG_WALL_BALANCE_PROFILE = {
  id: "hg-wall",
  displayName: "HG Wall",
  firstClearReward: { kind: "hov-core", amount: 400 },
  challenge: {
    hgDivisor: 1e10,
    hgExponent: 0.5,
  },
  reward: {
    targetClearMs: 20 * MINUTE_MS,
    baseExponentBonus: 0.01,
    minimumExponentBonus: 0.01,
    falloff: 0.677,
    caps: {
      base: 1,
      relaxed: 1,
    },
  },
} as const;

export const STOCK_WALL_BALANCE_PROFILE = {
  id: "stock-wall",
  displayName: "Stock Wall",
  firstClearReward: { kind: "nhov-first-branch-selection-unlock" },
  challenge: {
    baseNhovExecutionLimit: 5,
  },
  reward: {
    targetClearMs: 3 * MINUTE_MS,
    minimumNhovLimitBonus: 1,
    caps: {
      base: 10,
      relaxed: 10,
    },
  },
} as const;

export const AMPLIFICATION_WALL_BALANCE_PROFILE = {
  id: "amplification-wall",
  displayName: "Amplification Wall",
  firstClearReward: { kind: "amplifier-la-da-availability-unlock" },
  challenge: {
    lowerSourceGainMultiplierPerCorruptedEntry: 0.75,
  },
  reward: {
    targetClearMs: 30 * MINUTE_MS,
    minimumInfluence: 1.5,
    caps: {
      base: Number.POSITIVE_INFINITY,
      relaxed: Number.POSITIVE_INFINITY,
    },
  },
} as const;

export const WALL_BALANCE_PROFILES = {
  "step-wall": STEP_WALL_BALANCE_PROFILE,
  "price-wall": PRICE_WALL_BALANCE_PROFILE,
  "freshness-wall": FRESHNESS_WALL_BALANCE_PROFILE,
  "mp-wall": MP_WALL_BALANCE_PROFILE,
  "outer-wall": OUTER_WALL_BALANCE_PROFILE,
  "split-wall": SPLIT_WALL_BALANCE_PROFILE,
  "hg-wall": HG_WALL_BALANCE_PROFILE,
  "stock-wall": STOCK_WALL_BALANCE_PROFILE,
  "amplification-wall": AMPLIFICATION_WALL_BALANCE_PROFILE,
} as const satisfies Record<WallId, WallBalanceProfileCommon>;

export type WallBalanceProfile =
  (typeof WALL_BALANCE_PROFILES)[keyof typeof WALL_BALANCE_PROFILES];

export function getWallBalanceProfile(wallId: WallId): WallBalanceProfile {
  return WALL_BALANCE_PROFILES[wallId];
}
